/**
 * Amazon 商品管理: 画面側の純関数（フィールド値の取得/設定・差分・要約・NGヒント）。
 * 正は worker 側 src/amazon/content.ts。ここは画面のリアルタイム表示と mock 用の TS 版。
 * 属性は SP-API の attributes 形（{ attr: [ {value, language_tag, marketplace_id, ...} ] }）のまま扱う。
 */
import type { AmzAttributes, AmzChange, AmzFieldDef, AmzFieldKey, AmzIssue, AmzProductRow, AmzSource } from "../types/amazon";
import { AMZ_HIDDEN_ATTRS, AMZ_HIDDEN_PREFIXES, AMZ_IMAGE_ATTRS } from "../types/amazon";

export const DEFAULT_LANGUAGE_TAG = "ja_JP";

// ---- 値の型 --------------------------------------------------------------

export interface AmzMeasure {
  value: number | "";
  unit: string;
}

export interface AmzDimensions {
  length: AmzMeasure;
  width: AmzMeasure;
  height: AmzMeasure;
}

/** 画面が扱う単純値。json 型は属性配列そのもの（読み取り専用）。 */
export type AmzFieldValue = string | string[] | number | boolean | AmzMeasure | AmzDimensions | unknown[] | null;

type Entry = Record<string, unknown>;

// ---- キー判定 --------------------------------------------------------------

const BULLET_RE = /^(.+)\[(\d+)\]$/;

/** `bullet_point[2]` → `bullet_point`。それ以外はそのまま。 */
export function baseAttrKey(fieldKey: AmzFieldKey): string {
  const m = BULLET_RE.exec(fieldKey);
  return m ? m[1] : fieldKey;
}

/** `bullet_point[2]` → 2。添字なしは null。 */
export function fieldIndex(fieldKey: AmzFieldKey): number | null {
  const m = BULLET_RE.exec(fieldKey);
  return m ? Number(m[2]) : null;
}

export function bulletKey(i: number): AmzFieldKey {
  return `bullet_point[${i}]`;
}

export function isHiddenAttr(key: string): boolean {
  return (
    (AMZ_HIDDEN_ATTRS as readonly string[]).includes(key) ||
    AMZ_HIDDEN_PREFIXES.some((p) => key.startsWith(p))
  );
}

export function isImageAttr(key: string): boolean {
  return (AMZ_IMAGE_ATTRS as readonly string[]).includes(key);
}

export function codePointLength(s: string | null | undefined): number {
  return Array.from(s ?? "").length;
}

// ---- 低レベル --------------------------------------------------------------

function entries(attrs: AmzAttributes | null | undefined, key: string): Entry[] {
  const v = attrs?.[key];
  return Array.isArray(v) ? (v as Entry[]) : [];
}

function strOf(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function numOf(v: unknown): number | "" {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return "";
}

const IGNORED_COMPARE_KEYS = new Set(["marketplace_id", "language_tag"]);

/** キー順を無視した等価比較。marketplace_id / language_tag は比較対象外（画面から変えられないため）。 */
export function stableStringify(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(",")}]`;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .filter((k) => !IGNORED_COMPARE_KEYS.has(k) && o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(o[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v ?? null);
}

export function attrEqual(a: unknown[] | undefined, b: unknown[] | undefined): boolean {
  const ea = a ?? [];
  const eb = b ?? [];
  if (ea.length === 0 && eb.length === 0) return true;
  return stableStringify(ea) === stableStringify(eb);
}

function withAttr(attrs: AmzAttributes, key: string, list: unknown[] | null): AmzAttributes {
  const next: AmzAttributes = { ...attrs };
  if (list === null || list.length === 0) delete next[key];
  else next[key] = list;
  return next;
}

function baseEntry(def: AmzFieldDef, marketplaceId: string, extra: Entry): Entry {
  const e: Entry = { ...extra };
  if (def.hasLanguageTag) e.language_tag = DEFAULT_LANGUAGE_TAG;
  e.marketplace_id = marketplaceId;
  return e;
}

function measureOf(raw: unknown): AmzMeasure {
  const o = (raw ?? {}) as Entry;
  return { value: numOf(o.value), unit: strOf(o.unit) };
}

// ---- フィールド値の取得/設定 --------------------------------------------------

/**
 * fieldKey: 属性キー / `bullet_point[i]`。def 省略時は文字列として読む（value を持つ最初のエントリ）。
 * bullet_point（添字なし・multi）は string[]、`bullet_point[i]` は string。
 */
export function getFieldValue(
  attrs: AmzAttributes | null | undefined,
  fieldKey: AmzFieldKey,
  def?: AmzFieldDef
): AmzFieldValue {
  const idx = fieldIndex(fieldKey);
  const key = baseAttrKey(fieldKey);
  const list = entries(attrs, key);
  if (idx !== null) return strOf(list[idx]?.value);
  if (key === "images") return getImages(attrs);
  const type = def?.type ?? "text";
  switch (type) {
    case "text":
    case "textarea":
    case "enum":
      if (def?.multi) return list.map((e) => strOf(e.value));
      return strOf(list[0]?.value);
    case "number": {
      const n = numOf(list[0]?.value);
      return n === "" ? null : n;
    }
    case "boolean": {
      const v = list[0]?.value;
      return typeof v === "boolean" ? v : null;
    }
    case "measure":
      return list[0] ? measureOf(list[0]) : null;
    case "dimensions": {
      const e = list[0];
      if (!e) return null;
      return { length: measureOf(e.length), width: measureOf(e.width), height: measureOf(e.height) };
    }
    case "json":
    default:
      return list;
  }
}

function stripTrailingEmpty(list: string[]): string[] {
  const out = [...list];
  while (out.length > 0 && out[out.length - 1].trim() === "") out.pop();
  return out;
}

/** 新しい attributes を返す（元は変更しない）。空値は属性削除。json 型・editable=false 相当の呼び出しは呼び側で抑止。 */
export function setFieldValue(
  attrs: AmzAttributes,
  fieldKey: AmzFieldKey,
  value: AmzFieldValue,
  def: AmzFieldDef,
  marketplaceId: string
): AmzAttributes {
  const idx = fieldIndex(fieldKey);
  const key = baseAttrKey(fieldKey);
  const old = entries(attrs, key);

  // bullet_point[i]: 行単位。途中の空行は残し（Schema 検証で検出）、末尾の空行だけ落とす。
  if (idx !== null) {
    const rows = old.map((e) => strOf(e.value));
    while (rows.length <= idx) rows.push("");
    rows[idx] = strOf(value);
    return setStringList(attrs, key, stripTrailingEmpty(rows), def, marketplaceId, old);
  }

  switch (def.type) {
    case "text":
    case "textarea":
    case "enum": {
      if (def.multi) {
        const arr = Array.isArray(value) ? (value as string[]) : splitMulti(strOf(value));
        const cleaned = key === "bullet_point" ? stripTrailingEmpty(arr) : arr.map((s) => s.trim()).filter((s) => s !== "");
        return setStringList(attrs, key, cleaned, def, marketplaceId, old);
      }
      const s = strOf(value);
      if (s.trim() === "") return withAttr(attrs, key, null);
      return withAttr(attrs, key, [baseEntry(def, marketplaceId, { ...(old[0] ?? {}), value: s })]);
    }
    case "number": {
      const n = numOf(value);
      if (n === "") return withAttr(attrs, key, null);
      return withAttr(attrs, key, [baseEntry(def, marketplaceId, { ...(old[0] ?? {}), value: n })]);
    }
    case "boolean": {
      if (typeof value !== "boolean") return withAttr(attrs, key, null);
      return withAttr(attrs, key, [baseEntry(def, marketplaceId, { ...(old[0] ?? {}), value })]);
    }
    case "measure": {
      const m = (value ?? null) as AmzMeasure | null;
      const n = m ? numOf(m.value) : "";
      if (!m || n === "") return withAttr(attrs, key, null);
      return withAttr(attrs, key, [baseEntry(def, marketplaceId, { value: n, unit: m.unit })]);
    }
    case "dimensions": {
      const d = (value ?? null) as AmzDimensions | null;
      if (!d) return withAttr(attrs, key, null);
      const parts = (["length", "width", "height"] as const).map((k) => ({ k, n: numOf(d[k].value), unit: d[k].unit }));
      if (parts.every((p) => p.n === "")) return withAttr(attrs, key, null);
      const e: Entry = {};
      for (const p of parts) if (p.n !== "") e[p.k] = { value: p.n, unit: p.unit };
      return withAttr(attrs, key, [baseEntry(def, marketplaceId, e)]);
    }
    case "json":
    default:
      return attrs;
  }
}

function splitMulti(s: string): string[] {
  return s
    .split(/[;；、\n]/)
    .map((x) => x.trim())
    .filter((x) => x !== "");
}

function setStringList(
  attrs: AmzAttributes,
  key: string,
  list: string[],
  def: AmzFieldDef,
  marketplaceId: string,
  old: Entry[]
): AmzAttributes {
  if (list.length === 0) return withAttr(attrs, key, null);
  return withAttr(
    attrs,
    key,
    list.map((s, i) => baseEntry(def, marketplaceId, { ...(old[i] ?? {}), value: s }))
  );
}

/** AI提案の newValue（文字列）を draft に反映する。multi（bullet 以外）は区切り文字で分割する。 */
export function applyStringValue(
  attrs: AmzAttributes,
  fieldKey: AmzFieldKey,
  text: string,
  def: AmzFieldDef,
  marketplaceId: string
): AmzAttributes {
  if (fieldIndex(fieldKey) === null && def.multi && baseAttrKey(fieldKey) !== "bullet_point") {
    return setFieldValue(attrs, fieldKey, splitMulti(text), def, marketplaceId);
  }
  return setFieldValue(attrs, fieldKey, text, def, marketplaceId);
}

// ---- 画像 -------------------------------------------------------------------

export function getImages(attrs: AmzAttributes | null | undefined): string[] {
  const out: string[] = [];
  for (const k of AMZ_IMAGE_ATTRS) {
    const url = strOf(entries(attrs, k)[0]?.media_location);
    if (url.trim() !== "") out.push(url);
  }
  return out;
}

/** urls[i] を AMZ_IMAGE_ATTRS[i] に割り当てる（空は詰める。9枚まで）。 */
export function setImages(attrs: AmzAttributes, urls: string[], marketplaceId: string): AmzAttributes {
  const next: AmzAttributes = { ...attrs };
  const clean = urls.map((u) => u.trim()).filter((u) => u !== "").slice(0, AMZ_IMAGE_ATTRS.length);
  AMZ_IMAGE_ATTRS.forEach((k, i) => {
    if (i < clean.length) next[k] = [{ media_location: clean[i], marketplace_id: marketplaceId }];
    else delete next[k];
  });
  return next;
}

export function imageSlotName(i: number): string {
  return i === 0 ? "MAIN" : `SUB ${String(i).padStart(2, "0")}`;
}

export function summarizeImages(urls: string[]): string {
  return urls.map((u, i) => `${imageSlotName(i)}: ${u}`).join(" / ");
}

export function fileNameOf(url: string): string {
  const clean = url.split("?")[0];
  return clean.slice(clean.lastIndexOf("/") + 1) || url;
}

// ---- 要約（差分表示用の文字列化）----------------------------------------------

function entryText(e: Entry, def?: AmzFieldDef): string {
  const opt = (v: string) => def?.options?.find((o) => o.value === v)?.label ?? v;
  if ("value" in e && (typeof e.value !== "object" || e.value === null)) {
    const v = e.value;
    if (typeof v === "boolean") return v ? "はい" : "いいえ";
    if ("unit" in e && e.unit) return `${strOf(v)} ${strOf(e.unit)}`;
    return def?.type === "enum" ? opt(strOf(v)) : strOf(v);
  }
  if ("length" in e && "width" in e && "height" in e) {
    const l = measureOf(e.length);
    const w = measureOf(e.width);
    const h = measureOf(e.height);
    return `${l.value} × ${w.value} × ${h.value} ${l.unit}`.trim();
  }
  const rest: Entry = { ...e };
  delete rest.marketplace_id;
  delete rest.language_tag;
  return JSON.stringify(rest);
}

/** 属性 1 つの表示用文字列。未設定は ''。 */
export function summarizeValue(attrs: AmzAttributes | null | undefined, key: string, def?: AmzFieldDef): string {
  if (key === "images") return summarizeImages(getImages(attrs));
  const idx = fieldIndex(key);
  const list = entries(attrs, baseAttrKey(key));
  if (idx !== null) return strOf(list[idx]?.value);
  return list.map((e) => entryText(e, def)).join("、");
}

// ---- 差分 -------------------------------------------------------------------

const LABEL_OVERRIDE: Record<string, string> = {
  item_name: "商品タイトル",
  product_description: "商品説明",
};

export function labelForKey(key: string, fields: AmzFieldDef[]): string {
  const idx = fieldIndex(key);
  if (idx !== null) return `Bullet Point ${idx + 1}`;
  if (key === "images") return "商品画像";
  return LABEL_OVERRIDE[key] ?? fields.find((f) => f.key === key)?.label ?? key;
}

/**
 * current と draft の差分（属性キー単位の deep-equal）。bullet_point は `bullet_point[i]` 行ごと、画像は `images` 1件。
 * current が null（未登録SKU）のときは draft の全属性が差分になる。HIDDEN 属性は対象外。
 */
export function computeChanges(
  current: AmzAttributes | null,
  draft: AmzAttributes,
  fields: AmzFieldDef[],
  sources?: Record<AmzFieldKey, AmzSource>
): AmzChange[] {
  const defOf = (k: string) => fields.find((f) => f.key === k);
  const cur: AmzAttributes = current ?? {};
  const src = (k: string): AmzSource | null => sources?.[k] ?? null;
  const out: AmzChange[] = [];
  const push = (key: string, kind: AmzChange["kind"], oldValue: string, newValue: string) =>
    out.push({ key, label: labelForKey(key, fields), kind, oldValue, newValue, source: src(key) });

  if (!attrEqual(cur.item_name, draft.item_name)) {
    push("item_name", "text", summarizeValue(cur, "item_name"), summarizeValue(draft, "item_name"));
  }

  const curBullets = entries(cur, "bullet_point").map((e) => strOf(e.value));
  const draftBullets = entries(draft, "bullet_point").map((e) => strOf(e.value));
  const rows = Math.max(curBullets.length, draftBullets.length);
  for (let i = 0; i < rows; i++) {
    const a = curBullets[i] ?? "";
    const b = draftBullets[i] ?? "";
    if (a !== b) push(bulletKey(i), "text", a, b);
  }

  if (!attrEqual(cur.product_description, draft.product_description)) {
    push("product_description", "text", summarizeValue(cur, "product_description"), summarizeValue(draft, "product_description"));
  }

  const special = new Set(["item_name", "bullet_point", "product_description"]);
  const orderedKeys: string[] = [];
  for (const f of fields) if (!special.has(f.key)) orderedKeys.push(f.key);
  const rest = Array.from(new Set([...Object.keys(cur), ...Object.keys(draft)]))
    .filter((k) => !special.has(k) && !orderedKeys.includes(k))
    .sort();
  for (const key of [...orderedKeys, ...rest]) {
    if (isHiddenAttr(key) || isImageAttr(key)) continue;
    if (attrEqual(cur[key], draft[key])) continue;
    const d = defOf(key);
    push(key, "attribute", summarizeValue(cur, key, d), summarizeValue(draft, key, d));
  }

  const curImgs = getImages(cur);
  const draftImgs = getImages(draft);
  if (curImgs.join("\n") !== draftImgs.join("\n")) {
    push("images", "image", summarizeImages(curImgs), summarizeImages(draftImgs));
  }
  return out;
}

/** 差分のキーと新しい値から作る安定ハッシュ（FNV-1a 32bit hex）。 */
export function changesHash(changes: AmzChange[]): string {
  const text = changes
    .map((c) => `${c.key}\u0000${c.newValue}`)
    .sort()
    .join("\u0001");
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** 差分に含まれる属性名（`bullet_point[1]` → `bullet_point`、`images` → 画像属性群）。 */
export function changedAttrNames(changes: AmzChange[]): Set<string> {
  const s = new Set<string>();
  for (const c of changes) {
    if (c.key === "images") AMZ_IMAGE_ATTRS.forEach((k) => s.add(k));
    else s.add(baseAttrKey(c.key));
  }
  return s;
}

/** Issue が「下書きで修正済み」か（対象属性のいずれかが差分に含まれる）。 */
export function issueTouchedByDraft(issue: AmzIssue, changes: AmzChange[]): boolean {
  const names = changedAttrNames(changes);
  return issue.attributeNames.some((n) => names.has(n));
}

/** Issue の attributeNames[0] を AI 用 fieldKey にする（bullet_point は 'bullet_point[0]'）。 */
export function issueFieldKey(issue: AmzIssue): AmzFieldKey | null {
  const n = issue.attributeNames[0];
  if (!n) return null;
  return n === "bullet_point" ? bulletKey(0) : n;
}

// ---- 一覧の判定 --------------------------------------------------------------

export function needsReviewRow(r: Pick<AmzProductRow, "issues" | "syncError" | "draftChanges" | "registered">): boolean {
  return r.issues.error > 0 || r.issues.warning > 0 || !!r.syncError || r.draftChanges > 0 || !r.registered;
}

// ---- NG表現ヒント（画面用の簡易リスト。正は worker の NG Scanner）---------------------

const NG_WORDS: [string, string][] = [
  ["抗菌", "薬機法"],
  ["除菌", "薬機法"],
  ["滅菌", "薬機法"],
  ["殺菌", "薬機法"],
  ["治療", "薬機法"],
  ["効能", "薬機法"],
  ["No.1", "景表法"],
  ["日本一", "景表法"],
  ["世界一", "景表法"],
  ["業界初", "景表法"],
  ["絶対", "絶対表現ルール"],
  ["永久", "絶対表現ルール"],
  ["完全に", "絶対表現ルール"],
  ["100%", "絶対表現ルール"],
  ["完璧", "絶対表現ルール"],
  ["主婦", "呼称ルール"],
  ["奥様", "呼称ルール"],
];

export interface AmzNgHit {
  word: string;
  law: string;
}

export function ngHits(text: string | null | undefined): AmzNgHit[] {
  const t = text ?? "";
  return NG_WORDS.filter(([w]) => t.includes(w)).map(([word, law]) => ({ word, law }));
}

// ---- 表示補助 -----------------------------------------------------------------

/** 'YYYY-MM-DD HH:MM:SS'（UTC）→ 'YYYY-MM-DD HH:MM'（JST）。解釈できなければそのまま返す。 */
export function fmtJst(utc: string | null | undefined): string {
  if (!utc) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(utc);
  if (!m) return utc;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])) + 9 * 3600 * 1000;
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

export function truncateText(s: string, n = 36): string {
  const arr = Array.from(s);
  return arr.length > n ? `${arr.slice(0, n).join("")}…` : s;
}

export function formatYen(n: number | null | undefined): string {
  return n == null ? "—" : `¥${n.toLocaleString("ja-JP")}`;
}
